import type { Membership, ProjectPermission, SelfInfo } from '@/lib/api';

/**
 * Mirror of the server-side canWriteLang gate (internal/httpserver/api/permissions.go).
 * Used in the translator UI to short-circuit save buttons before the user
 * triggers a guaranteed-403 round trip.
 */
export function canWriteLang(
  self: SelfInfo | undefined,
  members: Membership[] | undefined,
  permissions: ProjectPermission[] | undefined,
  projectID: string,
  lang: string,
): boolean {
  if (!self) return false;
  if (self.kind === 'project') return self.projectId === projectID;
  if (!self.subject) return false;
  const me = (members ?? []).find((m) => m.userSubject === self.subject);
  if (!me) return false;
  if (me.role === 'owner' || me.role === 'admin') return true;
  if (me.role === 'viewer') return false;
  const grant = (permissions ?? []).find((p) => p.userSubject === self.subject);
  if (!grant) return false;
  return (grant.languages ?? []).some((l) => l.code === lang && l.access === 'write');
}

/**
 * True when the principal can write *any* language in the project. The
 * translator editor uses this to decide whether to render the save toolbar
 * at all for viewers / translators with read-only grants.
 */
export function canWriteAnyLang(
  self: SelfInfo | undefined,
  members: Membership[] | undefined,
  permissions: ProjectPermission[] | undefined,
  projectID: string,
  targetLangs: string[],
): boolean {
  return targetLangs.some((l) => canWriteLang(self, members, permissions, projectID, l));
}
